/**
 * Date presets for the filter rows, resolved in the reader's own day.
 *
 * The dates go into the URL as bare `YYYY-MM-DD` and the server widens each to
 * a whole day in Asia/Karachi, so the two ends of this agree by construction:
 * nothing here converts to UTC, and nothing on the server reads a bare date as
 * midnight UTC.
 *
 * Shared by the orders table and both dashboards (docs/ADMIN-PLAN.md slices 3
 * and 8) — two lists of presets is how "this week" comes to mean two different
 * weeks on two screens. It lives in lib/ rather than routes/ops/ for that
 * reason: the merchant portal is lazy-loaded separately and must not reach
 * into the ops group to ask what "last month" means.
 */

export interface DatePreset {
    value: string;
    label: string;
}

export const DATE_PRESETS: DatePreset[] = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_month', label: 'This month' },
    { value: 'last_month', label: 'Last month' },
];

export interface DateRange {
    from?: string;
    to?: string;
}

/**
 * Sets or clears one bound of a `DateRange` without ever writing an explicit
 * `undefined` into the object — `exactOptionalPropertyTypes` treats an
 * assigned `undefined` as different from an absent key.
 */
export function setDateBound(range: DateRange, bound: 'from' | 'to', value: string): DateRange {
    if (value === '') {
        const next = { ...range };
        delete next[bound];
        return next;
    }
    return { ...range, [bound]: value };
}

/**
 * `YYYY-MM-DD` in local time. `toISOString` would shift the day westward.
 *
 * Exported because the range picker hands back `Date` objects and has to
 * convert them the same way the presets do — two conversions would disagree by
 * a day for anyone west of UTC, and the preset dropdown would stop lighting up
 * for a range it had just written.
 */
export function isoDay(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${String(year)}-${month}-${day}`;
}

/**
 * The inverse of `isoDay`, for handing a URL's dates back to a calendar.
 *
 * Built from the parts rather than `new Date(iso)`: the string form parses as
 * UTC midnight, which is the previous day anywhere west of Greenwich — the
 * picker would open on the wrong date and, worse, writing it straight back
 * would shift the range by a day per round trip.
 */
export function fromIsoDay(iso: string | undefined): Date | null {
    if (iso === undefined) {
        return null;
    }

    const [year, month, day] = iso.split('-').map(Number);

    return year === undefined || month === undefined || day === undefined
        ? null
        : new Date(year, month - 1, day);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A URL parameter as a bare `YYYY-MM-DD`, or nothing.
 *
 * Every route that reads a date window off the URL validates it through here,
 * so a hand-edited or stale link narrows the screen or does nothing — never
 * 422s it against the API.
 */
export function isoDate(value: unknown): string | undefined {
    return typeof value === 'string' && ISO_DATE.test(value) ? value : undefined;
}

function daysAgo(count: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - count);

    return date;
}

export function presetRange(preset: string): DateRange {
    const today = isoDay(new Date());

    switch (preset) {
        case 'today':
            return { from: today, to: today };
        case 'yesterday': {
            const day = isoDay(daysAgo(1));
            return { from: day, to: day };
        }
        case 'this_month': {
            const now = new Date();
            return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
        }
        case 'last_month': {
            const now = new Date();
            /* Day 0 of this month is the last day of the previous one, so the
             * month arithmetic never has to know how long February is. */
            return {
                from: isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
                to: isoDay(new Date(now.getFullYear(), now.getMonth(), 0)),
            };
        }
        default:
            return {};
    }
}

/**
 * Whether a range in the URL is one a preset would have produced.
 *
 * Used to light the preset dropdown back up after a refresh: the URL carries
 * two dates, not the name of a preset, because a link that says "last 7 days"
 * would mean a different week when it is opened tomorrow.
 */
export function rangeMatches(range: DateRange, from?: string, to?: string): boolean {
    return range.from === from && range.to === to;
}
